import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileText, Loader2, Pen, Plus, Trash2, XCircle } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api-client';
import { SPECIALTY_LABELS, type AIContractAnalysis, type RiskLevel } from '@parecer/shared';
import { cn } from '@/lib/utils';

interface SignerRow {
  name: string;
  email: string;
}

interface ContractDetail {
  contractId: string;
  title: string;
  status: string;
  contractType?: string;
  downloadUrl?: string;
  createdAt: string;
  signatureStatus?: string;
  signatureRequest?: {
    requestId: string;
    provider: string;
    status: string;
    signers: Array<{ name: string; email: string }>;
    pendingProviderSetup?: boolean;
    requestedAt: string;
  };
}

type AnalysisStage = 'queued' | 'extracting_text' | 'analyzing_clauses' | 'persisting' | 'complete' | 'failed';

interface AnalysisResponse {
  status: string;
  analysisStage?: AnalysisStage | null;
  analysisError?: string | null;
  analysisStartedAt?: string | null;
  analysisCompletedAt?: string | null;
  analysis: AIContractAnalysis | null;
}

const STAGES: { key: AnalysisStage; label: string; description: string }[] = [
  { key: 'queued', label: 'Na fila', description: 'Aguardando processamento iniciar.' },
  { key: 'extracting_text', label: 'Extraindo texto', description: 'OCR/parsing do documento (Textract para PDF, Mammoth para DOCX).' },
  { key: 'analyzing_clauses', label: 'Analisando cláusulas', description: 'IA Claude identifica riscos, partes e sugestões.' },
  { key: 'persisting', label: 'Estruturando relatório', description: 'Salvando análise no banco e atualizando o card.' },
  { key: 'complete', label: 'Concluído', description: 'Análise pronta.' },
];

function stageIndex(stage?: AnalysisStage | null): number {
  if (!stage || stage === 'failed') return -1;
  return STAGES.findIndex((s) => s.key === stage);
}

export function ContratoDetalhe() {
  const { contractId } = useParams();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signers, setSigners] = useState<SignerRow[]>([{ name: '', email: '' }]);
  const [signMessage, setSignMessage] = useState('');

  const { data: contract } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => api.get<ContractDetail>(`/contracts/${contractId}`),
    enabled: Boolean(contractId),
  });

  const sigMut = useMutation({
    mutationFn: (body: { signers: SignerRow[]; message?: string }) =>
      api.post<{ pendingProviderSetup: boolean }>(`/contracts/${contractId}/signature-request`, body),
    onSuccess: (data) => {
      toast({
        variant: data.pendingProviderSetup ? 'warning' : 'success',
        title: data.pendingProviderSetup ? 'Solicitação registrada' : 'Convites enviados',
        description: data.pendingProviderSetup
          ? 'Integração com Clicksign está em finalização — você será notificado quando o convite for enviado.'
          : 'Os signatários receberam o convite por email.',
      });
      setSignDialogOpen(false);
      setSigners([{ name: '', email: '' }]);
      setSignMessage('');
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao solicitar',
        description: err instanceof Error ? err.message : 'Erro',
      }),
  });

  function submitSignatureRequest(e: React.FormEvent): void {
    e.preventDefault();
    const valid = signers.filter(
      (s) => s.name.trim().length >= 2 && /^.+@.+\..+$/.test(s.email.trim()),
    );
    if (valid.length === 0) {
      toast({ variant: 'warning', title: 'Adicione ao menos um signatário válido' });
      return;
    }
    sigMut.mutate({
      signers: valid.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
      ...(signMessage.trim() ? { message: signMessage.trim() } : {}),
    });
  }
  const { data: analysisRes } = useQuery({
    queryKey: ['contract', contractId, 'analysis'],
    queryFn: () => api.get<AnalysisResponse>(`/contracts/${contractId}/analysis`),
    enabled: Boolean(contractId),
    refetchInterval: (q) => {
      const d = q.state.data as AnalysisResponse | undefined;
      if (d?.status === 'COMPLETED' || d?.analysisStage === 'complete') return false;
      if (d?.analysisStage === 'failed') return false;
      // Snappy polling while progressing — every 2s — so the user feels movement.
      return 2000;
    },
  });

  const analysis = analysisRes?.analysis;
  const stage = analysisRes?.analysisStage ?? null;
  const failed = stage === 'failed';
  const idxNow = stageIndex(stage);
  const elapsedSeconds = analysisRes?.analysisStartedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(analysisRes.analysisStartedAt).getTime()) / 1000))
    : null;

  return (
    <AppShell role="users">
      <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-display-lg">{contract?.title ?? 'Carregando…'}</h1>
            {contract?.contractType && (
              <p className="text-muted-foreground mt-1">{contract.contractType}</p>
            )}
          </div>
          <div className="flex gap-2">
            {contract?.downloadUrl && (
              <Button asChild variant="outline" size="sm">
                <a href={contract.downloadUrl} download>
                  <Download className="h-4 w-4 mr-2" />
                  Baixar
                </a>
              </Button>
            )}
            <Button
              variant="accent"
              size="sm"
              onClick={() => setSignDialogOpen(true)}
              disabled={
                contract?.signatureStatus === 'pending' ||
                contract?.signatureStatus === 'signed'
              }
            >
              <Pen className="h-4 w-4 mr-2" />
              {contract?.signatureStatus === 'pending'
                ? 'Em assinatura'
                : contract?.signatureStatus === 'signed'
                  ? 'Assinado'
                  : 'Enviar pra assinatura'}
            </Button>
          </div>
        </div>

        {contract?.signatureRequest && (
          <Card className="mt-6 border-accent/30 bg-accent/[0.04]">
            <CardContent className="pt-5">
              <p className="font-medium flex items-center gap-2">
                <Pen className="h-4 w-4 text-accent" />
                Solicitação de assinatura
                {contract.signatureRequest.pendingProviderSetup && (
                  <Badge variant="warning" className="text-[10px]">Aguardando integração</Badge>
                )}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Provedor: <strong className="text-foreground">{contract.signatureRequest.provider}</strong> ·{' '}
                {contract.signatureRequest.signers.length} signatário(s):
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {contract.signatureRequest.signers.map((s) => (
                  <li key={s.email} className="flex items-center gap-2 text-muted-foreground">
                    <span className="font-medium text-foreground">{s.name}</span>
                    <span>·</span>
                    <span>{s.email}</span>
                  </li>
                ))}
              </ul>
              {contract.signatureRequest.pendingProviderSetup && (
                <p className="text-xs text-muted-foreground mt-3 italic">
                  Integração nativa com Clicksign está em finalização. Por enquanto, a equipe
                  Parecer.ai envia os convites manualmente — você é notificado quando todos
                  assinarem. Sem interrupção do fluxo.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {!analysis && analysisRes?.status !== 'COMPLETED' && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  {failed ? (
                    <XCircle className="h-5 w-5 text-danger" />
                  ) : (
                    <Loader2 className="h-5 w-5 text-accent animate-spin" />
                  )}
                  {failed ? 'Análise falhou' : 'Análise IA em andamento'}
                </span>
                {elapsedSeconds !== null && !failed && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {elapsedSeconds < 60
                      ? `${elapsedSeconds}s decorridos`
                      : `${Math.floor(elapsedSeconds / 60)}min ${elapsedSeconds % 60}s decorridos`}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {failed && analysisRes?.analysisError && (
                <p className="text-sm text-danger mb-4">
                  Erro: {analysisRes.analysisError}. Tente subir o documento novamente, ou contate o suporte.
                </p>
              )}
              <ul className="space-y-2">
                {STAGES.filter((s) => s.key !== 'complete').map((s, i) => {
                  const isPast = i < idxNow;
                  const isCurrent = i === idxNow;
                  return (
                    <li
                      key={s.key}
                      className={cn(
                        'flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm',
                        isPast && !failed && 'border-success/30 bg-success/5 text-muted-foreground',
                        isCurrent && !failed && 'border-accent/50 bg-accent/5',
                        !isPast && !isCurrent && 'border-border/60 text-muted-foreground',
                      )}
                    >
                      {isPast && !failed ? (
                        <CheckCircle2 className="h-4 w-4 text-success flex-none mt-0.5" />
                      ) : isCurrent && !failed ? (
                        <Loader2 className="h-4 w-4 text-accent flex-none mt-0.5 animate-spin" />
                      ) : (
                        <span className="h-4 w-4 rounded-full border border-current/40 flex-none mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{s.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {!failed && elapsedSeconds !== null && elapsedSeconds > 120 && (
                <p className="text-xs text-warning mt-4 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-none" />
                  Tempo acima do esperado (&gt; 2 min). Pode estar em fila ou com contrato muito longo. Se passar de 5 min sem mudar, recarregue a página ou contate o suporte.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar pra assinatura</DialogTitle>
              <DialogDescription>
                Os signatários recebem email com link pra assinar via Clicksign. Assinatura é
                vinculante (Lei 14.063/2020) e fica anexada ao contrato.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitSignatureRequest} className="space-y-4">
              <div className="space-y-2">
                <Label>Signatários</Label>
                {signers.map((s, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <Input
                      placeholder="Nome completo"
                      value={s.name}
                      onChange={(e) => {
                        const next = [...signers];
                        next[idx] = { ...next[idx]!, name: e.target.value };
                        setSigners(next);
                      }}
                    />
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      value={s.email}
                      onChange={(e) => {
                        const next = [...signers];
                        next[idx] = { ...next[idx]!, email: e.target.value };
                        setSigners(next);
                      }}
                    />
                    {signers.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:text-danger flex-none"
                        onClick={() => setSigners(signers.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSigners([...signers, { name: '', email: '' }])}
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Adicionar signatário
                </Button>
              </div>
              <div>
                <Label htmlFor="sig-message">Mensagem ao signatário (opcional)</Label>
                <textarea
                  id="sig-message"
                  rows={2}
                  maxLength={500}
                  className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Ex: Por favor, assine até o final da semana."
                  value={signMessage}
                  onChange={(e) => setSignMessage(e.target.value)}
                />
              </div>
              <p className="text-[11px] text-muted-foreground rounded-md border border-warning/30 bg-warning/[0.04] p-2">
                <strong>Atenção:</strong> a integração nativa com Clicksign está em finalização. Sua
                solicitação será registrada e nossa equipe envia o convite manualmente até o
                provedor terminar. Sem custo extra.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSignDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="accent" loading={sigMut.isPending}>
                  Solicitar assinatura
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {analysis && (
          <div className="mt-8 space-y-6">
            <div className="rounded-md border border-warning/40 bg-warning/[0.04] p-3 text-xs flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 flex-none" />
              <p className="text-muted-foreground">
                <strong className="text-foreground">Esta análise é informativa.</strong>{' '}
                Não substitui consulta com advogado nem constitui parecer jurídico. Não há relação
                cliente-advogado nesta etapa — a IA pode interpretar cláusulas atípicas de forma
                incorreta. Sempre valide com um profissional antes de tomar decisões.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Resumo
                  <RiskBadge level={analysis.overallRisk} />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-pretty">{analysis.summary}</p>
                <div className="flex flex-wrap gap-2 pt-2 text-xs">
                  <Badge variant="muted">Tipo: {analysis.contractType}</Badge>
                  <Badge variant="muted">Especialidade: {SPECIALTY_LABELS[analysis.recommendedSpecialty]}</Badge>
                  {analysis.parties.map((p) => (
                    <Badge key={p} variant="outline">{p}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <h2 className="font-serif text-2xl">Cláusulas analisadas</h2>
            {analysis.clauses.map((c) => (
              <Card key={c.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-lg">{c.title}</h3>
                    <RiskBadge level={c.riskLevel} />
                  </div>
                  <blockquote className="mt-3 border-l-2 border-accent/40 pl-4 italic text-sm text-muted-foreground">
                    {c.text}
                  </blockquote>
                  {c.concerns.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Preocupações
                      </h4>
                      <ul className="mt-2 space-y-1 text-sm">
                        {c.concerns.map((concern, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-warning">→</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {c.suggestions.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sugestões</h4>
                      <ul className="mt-2 space-y-1 text-sm">
                        {c.suggestions.map((s, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-success">+</span>
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            <Card className="bg-primary text-primary-foreground border-0">
              <CardContent className="p-6 flex items-center justify-between gap-6 flex-wrap">
                <div>
                  <h3 className="font-serif text-xl">Pronto para a revisão profissional?</h3>
                  <p className="text-sm text-primary-foreground/70 mt-1">
                    Contrate um advogado especialista em {SPECIALTY_LABELS[analysis.recommendedSpecialty]}.
                  </p>
                </div>
                <Button asChild variant="accent">
                  <Link to={`/advogados?specialty=${analysis.recommendedSpecialty}`}>Ver advogados</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/** Signature request dialog — used in the page above. */
function SignatureDialog() {
  // Inlined into the main component to keep state colocated; this file already
  // hosts the dialog usage via state hooks.
  return null;
}
void SignatureDialog;

function RiskBadge({ level }: { level: RiskLevel }) {
  const variant = level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'success';
  const label = level === 'high' ? 'Alto risco' : level === 'medium' ? 'Médio risco' : 'Baixo risco';
  return <Badge variant={variant}>{label}</Badge>;
}
